import React, { useState, useEffect, useContext } from "react";

import {
  Box,
  IconButton,
  Paper,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Table,
  Stack,
  Button,
  Tooltip,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import { isEmpty } from "lodash";
import { DataContext } from "../../../context/DataContext";
import { isAccess, simpleMessage } from "../../../helpers/Helpers";
import { deleteToken, deleteUserData, getToken } from "../../../api/Account";
import NoData from "../../../components/NoData";
import ListFooter from "../../../components/ui/ListFooter";
import SmallModal from "../../../components/modals/SmallModal";
import MediumModal from "../../../components/modals/MediumModal";

import Swal from "sweetalert2";
import {
  deletePeriodicidadAsync,
  getPeriodicidadListAsync,
} from "../../../api/Periodicidad";
import AddPeriodicidad from "./AddPeriodicidad";
import EditPeriodicidad from "./EditPeriodicidad";
import PlazosManager from "./plazos/PlazosManager";
import { getPlazoByPeriodicidadAsync } from "../../../api/Plazo";
import { monoFont, getTokens } from "../../../theme";

const PeriodicidadList = () => {
  const [periodicidadList, setPeriodicidadList] = useState([]);
  const [selectedPeriodicidad, setSelectedPeriodicidad] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPlazosModal, setShowPlazosModal] = useState(false);

  const {
    reload,
    setReload,
    access,
    setIsLoading,
    setIsLogged,
    setIsUnautorized,
    isDarkMode,
  } = useContext(DataContext);
  const t = getTokens(isDarkMode);

  const token = getToken();

  // Los plazos de cada periodicidad se muestran siempre en la tabla, asi que
  // se cargan junto con la lista (una llamada por periodicidad, en paralelo).
  const [plazosPorPeriodicidad, setPlazosPorPeriodicidad] = useState({});

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const result = await getPeriodicidadListAsync(token);
      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }

      const lista = result.data.result || [];
      setPeriodicidadList(lista);

      const respuestas = await Promise.all(
        lista.map((periodicidad) => getPlazoByPeriodicidadAsync(token, periodicidad.id))
      );

      const mapa = {};
      lista.forEach((periodicidad, indice) => {
        const respuesta = respuestas[indice];
        mapa[periodicidad.id] = respuesta.statusResponse
          ? respuesta.data.result || []
          : [];
      });
      setPlazosPorPeriodicidad(mapa);

      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  // Tras un alta/edicion/baja de plazos se refresca solo la periodicidad
  // afectada, no toda la tabla.
  const recargarPlazos = async (periodicidadId) => {
    const result = await getPlazoByPeriodicidadAsync(token, periodicidadId);
    if (!result.statusResponse) {
      return;
    }
    setPlazosPorPeriodicidad((previo) => ({
      ...previo,
      [periodicidadId]: result.data.result || [],
    }));
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsperPage] = useState(10);
  const indexLast = currentPage * itemsperPage;
  const indexFirst = indexLast - itemsperPage;
  const currentItem = periodicidadList.slice(indexFirst, indexLast);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const deletePeriodicidad = async (item) => {
    Swal.fire({
      title: "¿Estas Seguro?",
      text: "No podras revertir esto...!",
      icon: "warning",
      showCancelButton: true,

      confirmButtonColor: "#1d4ed8",
      cancelButtonColor: "#d83a3f",

      confirmButtonText: "Eliminar!",
      cancelButtonText: "Cancelar",
      customClass: {
        popup: "border",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        (async () => {
          setIsLoading(true);
          const result = await deletePeriodicidadAsync(token, item.id);
          if (!result.statusResponse) {
            setIsLoading(false);

            if (result.error === "eX01") {
              deleteUserData();
              deleteToken();
              setIsLogged(false);
              return;
            }

            if (result.error === "Unauthorized") {
              setIsUnautorized(true);
              return;
            }
            simpleMessage(result.error, "error");
            return;
          }

          setIsLoading(false);
          setReload(!reload);
          Swal.fire("Eliminado!", "Periodicidad eliminada.", "success");
        })();
      }
    });
  };

  const actionButtonSx = {
    width: 32,
    height: 32,
    borderRadius: "9px",
    border: `1px solid ${t.fieldLine}`,
    backgroundColor: t.field,
  };

  const puedeAccionar =
    isAccess(access, "CONFIG UPDATE") || isAccess(access, "CONFIG DELETE");

  return (
    <div>
      <Paper sx={{ overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="flex-end"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          flexWrap="wrap"
          sx={{ padding: "18px 22px", borderBottom: "1px solid", borderColor: "divider" }}
        >
          {isAccess(access, "CONFIG CREATE") ? (
            <Button
              onClick={() => setShowModal(true)}
              startIcon={<AddRoundedIcon />}
              variant="contained"
              color="primary"
              sx={{ whiteSpace: "nowrap", height: "44px" }}
            >
              Agregar periodicidad
            </Button>
          ) : null}
        </Stack>

        {isEmpty(currentItem) ? (
          <NoData />
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">#</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="center">Cantidad de días</TableCell>
                    <TableCell align="center">Cuota días</TableCell>
                    <TableCell align="center">Plazos</TableCell>
                    {puedeAccionar ? <TableCell align="center">Acciones</TableCell> : null}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {currentItem.map((row) => {
                    const { id, description, cuotaDias, cantidadDias } = row;
                    const plazos = plazosPorPeriodicidad[id];

                    return (
                      <TableRow key={id} hover>
                        <TableCell align="center" sx={{ color: "text.disabled", fontWeight: 600 }}>
                          {id}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>{description}</TableCell>
                        <TableCell align="center" sx={{ color: "text.secondary" }}>
                          {cantidadDias}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "text.secondary" }}>
                          {cuotaDias}
                        </TableCell>

                        <TableCell align="center">
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block",
                              minWidth: 28,
                              padding: "3px 10px",
                              borderRadius: "20px",
                              backgroundColor: t.field,
                              border: `1px solid ${t.fieldLine}`,
                              fontFamily: monoFont,
                              fontWeight: 700,
                              fontSize: 12.5,
                              color: isEmpty(plazos) ? "text.disabled" : "primary.main",
                            }}
                          >
                            {isEmpty(plazos) ? 0 : plazos.length}
                          </Box>
                        </TableCell>

                        {puedeAccionar ? (
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <Tooltip title="Gestionar plazos">
                                <IconButton
                                  size="small"
                                  sx={{ ...actionButtonSx, color: "primary.main" }}
                                  onClick={() => {
                                    setSelectedPeriodicidad(row);
                                    setShowPlazosModal(true);
                                  }}
                                >
                                  <FormatListNumberedRoundedIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>

                              {isAccess(access, "CONFIG UPDATE") ? (
                                <Tooltip title="Editar">
                                  <IconButton
                                    size="small"
                                    sx={{ ...actionButtonSx, color: "warning.main" }}
                                    onClick={() => {
                                      setSelectedPeriodicidad(row);
                                      setShowEditModal(true);
                                    }}
                                  >
                                    <EditRoundedIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                              {isAccess(access, "CONFIG DELETE") ? (
                                <Tooltip title="Eliminar">
                                  <IconButton
                                    size="small"
                                    sx={{ ...actionButtonSx, color: "error.main" }}
                                    onClick={() => deletePeriodicidad(row)}
                                  >
                                    <DeleteRoundedIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </Stack>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <ListFooter
              data={periodicidadList}
              currentCount={currentItem.length}
              indexFirst={indexFirst}
              paginate={paginate}
              itemsperPage={itemsperPage}
              noun="periodicidades"
            />
          </>
        )}
      </Paper>

      <SmallModal
        titulo={"Agregar Periodicidad"}
        isVisible={showModal}
        setVisible={setShowModal}
      >
        <AddPeriodicidad setShowModal={setShowModal} />
      </SmallModal>

      <SmallModal
        titulo={`Editar: ${selectedPeriodicidad.description}`}
        isVisible={showEditModal}
        setVisible={setShowEditModal}
      >
        <EditPeriodicidad
          selected={selectedPeriodicidad}
          setShowModal={setShowEditModal}
        />
      </SmallModal>

      <MediumModal
        titulo={`Plazos de: ${selectedPeriodicidad.description}`}
        isVisible={showPlazosModal}
        setVisible={setShowPlazosModal}
      >
        <PlazosManager
          periodicidad={selectedPeriodicidad}
          onChanged={() => recargarPlazos(selectedPeriodicidad.id)}
        />
      </MediumModal>
    </div>
  );
};

export default PeriodicidadList;
